import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, FileText, RefreshCw, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { OfflineDataBanner } from '@/components/ui/OfflineDataBanner';
import { SedContractDetails } from '@/components/sed/SedContractDetails';
import { SedDocCard } from '@/components/sed/SedDocCard';
import { SedLetterDetails } from '@/components/sed/SedLetterDetails';
import { SedQuickFilters } from '@/components/sed/SedQuickFilters';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { eventBusHub } from '@/lib/eventBusHub';
import { onDataChanged } from '@/lib/dataEvents';
import { friendlyError } from '@/lib/errors';
import {
  countSedFilters,
  filterSedDocuments,
  loadSedWorkspace,
  searchSedDocuments,
  sedWorkspaceCacheKey,
  type SedDocument,
  type SedQuickFilter,
  type SedSnapshot,
} from '@/lib/sed';
import { useAuthStore } from '@/store/authStore';
import { cn } from '@/lib/utils';

type SedTab = 'contracts' | 'letters';

export function SedWorkspacePage() {
  const userId = String(useAuthStore((state) => state.user?.id || ''));
  const { isMobile } = useBreakpoint();
  const [snapshot, setSnapshot] = useState<SedSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fromCache, setFromCache] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<SedTab>('contracts');
  const [filter, setFilter] = useState<SedQuickFilter>('all');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const reloadTimer = useRef<number | null>(null);

  const load = useCallback(async (mode: 'initial' | 'refresh' | 'silent') => {
    if (mode === 'initial') setLoading(true);
    if (mode === 'refresh') setRefreshing(true);
    try {
      const result = await loadSedWorkspace();
      setSnapshot(result.data);
      setFromCache(result.fromCache);
      setError('');
    } catch (cause) {
      setError(friendlyError(cause));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load('initial');
  }, [load]);

  const scheduleSilentReload = useCallback(() => {
    if (reloadTimer.current !== null) window.clearTimeout(reloadTimer.current);
    reloadTimer.current = window.setTimeout(() => {
      reloadTimer.current = null;
      void load('silent');
    }, 400);
  }, [load]);

  useEffect(() => {
    const unsubscribeBus = eventBusHub.subscribe((event) => {
      const topic = String(event?.topic || '');
      if (topic.startsWith('data.document.sed_')) scheduleSilentReload();
    });
    const unsubscribeLocal = onDataChanged((detail) => {
      if (detail.table === 'sed_contracts' || detail.table === 'sed_letters') scheduleSilentReload();
    });
    return () => {
      unsubscribeBus();
      unsubscribeLocal();
      if (reloadTimer.current !== null) {
        window.clearTimeout(reloadTimer.current);
        reloadTimer.current = null;
      }
    };
  }, [scheduleSilentReload]);

  const documents: SedDocument[] = useMemo(() => {
    if (!snapshot) return [];
    return tab === 'contracts' ? snapshot.contracts : snapshot.letters;
  }, [snapshot, tab]);

  const counts = useMemo(() => countSedFilters(documents, userId), [documents, userId]);

  const visible = useMemo(
    () => searchSedDocuments(filterSedDocuments(documents, filter, userId), query),
    [documents, filter, userId, query],
  );

  const selected = useMemo(
    () => documents.find((doc) => doc.id === selectedId) || null,
    [documents, selectedId],
  );

  const changeTab = useCallback((next: SedTab) => {
    setTab(next);
    setSelectedId('');
    if (next === 'letters' && filter === 'signed') setFilter('all');
  }, [filter]);

  const changeFilter = useCallback((next: SedQuickFilter) => {
    setFilter(next);
    setSelectedId('');
  }, []);

  useEffect(() => {
    if (isMobile) return;
    if (selectedId && documents.some((doc) => doc.id === selectedId)) return;
    setSelectedId(visible[0]?.id || '');
  }, [isMobile, selectedId, documents, visible]);

  const overdueCount = counts.overdue;

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <header className="shrink-0 space-y-2 border-b bg-background/95 px-3 py-2.5 backdrop-blur">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <FileText className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-sm font-semibold leading-5">Документооборот</h1>
            <p className="truncate text-[11px] text-muted-foreground">
              Договоры, письма, согласование и сроки
              {overdueCount > 0 ? ` · просрочено: ${overdueCount}` : ''}
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-9 w-9 shrink-0 rounded-xl p-0"
            onClick={() => void load('refresh')}
            disabled={refreshing}
            aria-label="Обновить документы"
          >
            <RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} />
          </Button>
        </div>

        <div className="flex items-center gap-1 rounded-xl border bg-muted/30 p-0.5">
          <button
            type="button"
            onClick={() => changeTab('contracts')}
            className={cn(
              'flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg text-xs font-medium transition-colors',
              tab === 'contracts' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            Договоры
            <span className="rounded-full bg-muted px-1.5 text-[10px] tabular-nums">{snapshot?.contracts.length || 0}</span>
          </button>
          <button
            type="button"
            onClick={() => changeTab('letters')}
            className={cn(
              'flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg text-xs font-medium transition-colors',
              tab === 'letters' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            Письма
            <span className="rounded-full bg-muted px-1.5 text-[10px] tabular-nums">{snapshot?.letters.length || 0}</span>
          </button>
        </div>

        <label className="relative block">
          <span className="sr-only">Поиск по документам</span>
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Номер, тема, контрагент…"
            className="h-9 rounded-xl pl-8 pr-8 text-xs"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
              aria-label="Очистить поиск"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </label>

        <SedQuickFilters
          value={filter}
          counts={counts}
          showSigned={tab === 'contracts'}
          onChange={changeFilter}
        />

        {fromCache && <OfflineDataBanner sourceKey={sedWorkspaceCacheKey()} />}

        {error && (
          <div className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-2.5 py-1.5 text-[11px] text-red-700">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span className="flex-1">{error}</span>
            <button
              type="button"
              onClick={() => void load('refresh')}
              className="shrink-0 rounded-md border border-red-500/30 px-1.5 py-0.5 text-[10px] hover:bg-red-500/10"
            >
              Повторить
            </button>
          </div>
        )}
      </header>

      <div className="min-h-0 flex-1 md:grid md:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
        <section
          className={cn(
            'min-h-0 space-y-2 overflow-y-auto p-3 md:border-r',
            selected && isMobile && 'hidden',
          )}
        >
          {loading && !snapshot && (
            <div className="space-y-2">
              {[0, 1, 2].map((key) => (
                <div key={key} className="h-24 animate-pulse rounded-2xl border bg-muted/40" />
              ))}
            </div>
          )}

          {!loading && !snapshot && !error && (
            <p className="rounded-xl border bg-card px-3 py-6 text-center text-xs text-muted-foreground">
              Документы не загружены.
            </p>
          )}

          {!loading && visible.length === 0 && snapshot && (
            <div className="rounded-2xl border bg-card px-3 py-8 text-center">
              <FileText className="mx-auto h-7 w-7 text-muted-foreground/60" />
              <p className="mt-2 text-xs font-medium">Ничего не найдено</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {filter === 'all' && !query
                  ? tab === 'contracts'
                    ? 'В базе пока нет договоров.'
                    : 'В базе пока нет писем.'
                  : 'Измените быстрый фильтр или запрос.'}
              </p>
            </div>
          )}

          {visible.map((doc) => (
            <SedDocCard
              key={`${doc.kind}-${doc.id}`}
              doc={doc}
              active={doc.id === selectedId}
              onClick={() => setSelectedId(doc.id)}
            />
          ))}

          {visible.length > 0 && (
            <p className="pb-1 text-center text-[10px] text-muted-foreground">
              Показано: {visible.length} из {documents.length}
            </p>
          )}
        </section>

        <section className={cn('min-h-0', !selected && isMobile && 'hidden')}>
          {selected?.kind === 'contract' && (
            <SedContractDetails
              contract={selected}
              onBack={isMobile ? () => setSelectedId('') : undefined}
            />
          )}
          {selected?.kind === 'letter' && (
            <SedLetterDetails
              letter={selected}
              onBack={isMobile ? () => setSelectedId('') : undefined}
            />
          )}
          {!selected && !isMobile && (
            <div className="flex h-full items-center justify-center p-6 text-center">
              <div>
                <FileText className="mx-auto h-8 w-8 text-muted-foreground/50" />
                <p className="mt-2 text-sm font-medium">Выберите документ</p>
                <p className="mt-1 max-w-xs text-[11px] text-muted-foreground">
                  В карточке — контрагент, сумма, срок, статус, маршрут согласования и файлы.
                </p>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
